import React from "react";

import {
    DragDropContext,
    Draggable,
    DropResult,
    Droppable,
    DroppableProvided,
} from "@hello-pangea/dnd";
import {produce} from "immer";

import * as dialogs from "@client/features/dialogs";
import {annotateWithExercises, useWorkout} from "@client/features/workouts";

import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {DragHandler} from "@client/components/DragHandler";
import {ExerciseSetsPreview} from "@client/components/ExerciseSet";
import {Icon} from "@client/components/Icon";
import {QuestionTypeSelector} from "@client/components/QuestionTypeSelector";
import {TemplateDialog} from "@client/components/SectionDialog";
import {
    ProgramUser,
    Workout,
    getPercentageExercises,
    processResponse,
} from "@client/components/Workout";
import {useAsyncFn, useDebounce} from "@client/hooks";
import {Breadcrumbs, ManagerLayout, ManagerTabs} from "@client/site/ManagerLayout";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {classNames, reverse, server} from "@reactivated";

const Card = {isComplete: ""} as const;

type GroupFormHandler = forms.PFormHandler<server.journal.workouts.WorkoutGroupForm>;

const GroupRoute = (props: {
    group: server.journal.workouts.WorkoutGroup.output | null;
    onSubmit: (values: GroupFormHandler["values"]) => void;
    back: () => void;
}) => {
    const form = forms.useAutoPform(
        server.journal.workouts.WorkoutGroupForm,
        props.group,
    );

    React.useEffect(() => {
        if (form.values.name == "") {
            form.setErrors({name: ["Name is required"]});
        } else {
            form.setErrors({name: undefined});
        }
    }, [form.values.name]);
    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                props.onSubmit(form.values);
            }}
            className="flex flex-col gap-4"
        >
            <h1 className="text-xl">
                {props.group == null ? "Create group" : "Update group"}
            </h1>
            <forms.PField field={form.fields.name} />
            <forms.PField field={form.fields.description} />
            <div className="space-x-2">
                <button
                    type="submit"
                    disabled={form.values.name == ""}
                    className="
                        rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold
                        text-white shadow-xs
                        hover:bg-indigo-500
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-indigo-600 focus-visible:outline-solid
                        disabled:border-slate-200 disabled:bg-slate-50
                        disabled:text-slate-500 disabled:shadow-none
                    "
                >
                    Save
                </button>
                <button
                    onClick={() => {
                        props.back();
                    }}
                    className="
                        rounded-sm bg-white px-2 py-1 text-xs font-semibold
                        text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                        hover:bg-gray-50
                    "
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};

function UpdateWorkout(props: {
    program_id: number;
    embed?: {url: string; user_id: number};
    group: server.journal.workouts.WorkoutGroup.output;
    workout: server.journal.workouts.Workout.output;
    back: () => void;
}) {
    const handlers = useWorkout({
        workout: {
            sections: props.workout.sections,
            questions: props.workout.questions,
        },
    });

    const percentageExercises = getPercentageExercises(handlers.exercises);

    const [workout, setWorkout] = React.useState(props.workout);
    const workoutForm = forms.usePform(
        {
            name: {type: "text"},
            instructions: {type: "textarea"},
            maxes: {
                label: "How do you want to handle percentages?",
                type: "select",
                widget: "select",
                required: true,
                options: [
                    ["EXISTING_MAXES", "Use existing maxes"],
                    ["UPDATE_MAXES", "Request updated maxes"],
                    ["AUTOMATIC", "Automatic"],
                ],
            },
        },
        {
            ...workout,
            name: workout.name ?? "",
            maxes: workout.maxes,
        },
        (values) => {
            setWorkout({...workout, ...values});
        },
    );

    const [state, doSave] = useAsyncFn(async () => {
        const response = await server.journal.workouts.update_workout({
            uuid: props.workout.uuid,
            sections: handlers.sections,
            questions: handlers.questions,
            template: {
                name: workoutForm.values.name,
                instructions: workoutForm.values.instructions,
                visibility: "PRIVATE",
                maxes: workoutForm.values.maxes,
            },
        });

        if (response.type == "invalid") {
            const errors = response.errors;

            const errorsMap = Object.fromEntries(
                errors
                    .filter((e) => e.loc.length == 2 && e.loc[0] == "template")
                    .map((e) => [e.loc[1], [e.msg]]),
            );
            workoutForm.setErrors(errorsMap);
        } else {
            if (props.embed != null) {
                window.location.href = props.embed.url;
            } else {
                window.location.href = reverse("program_detail", {
                    program_id: props.program_id,
                });
            }
        }
    }, [handlers.exercises, handlers.questions, workoutForm.values]);

    const createQuestion = (
        questionType: server.journal.workouts.QuestionType.output,
    ) => {
        handlers.setQuestions([
            ...handlers.questions,
            {
                uuid: crypto.randomUUID(),
                type: questionType,
                answer: "",
            },
        ]);
    };

    const save = (
        <div className="space-x-2">
            <button
                type="button"
                disabled={state.loading}
                onClick={doSave}
                className="
                    rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white
                    shadow-xs
                    hover:bg-indigo-500
                    focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-indigo-600 focus-visible:outline-solid
                    disabled:border-slate-200 disabled:bg-slate-50
                    disabled:text-slate-500 disabled:shadow-none
                "
            >
                {state.loading ? "Saving..." : "Save"}
            </button>
            <button
                onClick={() => {
                    history.pushState({}, "", window.location.pathname);
                    props.back();
                }}
                className="
                    rounded-sm bg-white px-2 py-1 text-xs font-semibold text-gray-900
                    shadow-xs ring-1 ring-gray-300 ring-inset
                    hover:bg-gray-50
                "
            >
                Cancel
            </button>
        </div>
    );

    const survey = (
        <>
            {handlers.questions.map((question, questionIndex) => {
                return (
                    <div key={question.uuid} className="flex items-center gap-2 py-1">
                        <div
                            aria-hidden="true"
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-blue-100 text-sm font-semibold
                            "
                        >
                            {questionIndex + 1}
                        </div>
                        <div className="flex-1">{question.type.name}</div>
                        <div>
                            <button
                                type="button"
                                onClick={() => {
                                    handlers.setQuestions(
                                        handlers.questions.filter(
                                            (q) => q.uuid != question.uuid,
                                        ),
                                    );
                                }}
                                className="
                                    rounded-full bg-white p-2 text-xs font-semibold
                                    text-gray-900 shadow-xs ring-1 ring-gray-300
                                    ring-inset
                                    hover:bg-gray-50
                                "
                            >
                                <Icon icon="custom-bolder-cross" />
                            </button>
                        </div>
                    </div>
                );
            })}
            <QuestionTypeSelector
                onSelectQuestionType={createQuestion}
                selectLabel="Add"
            >
                {({showQuestionTypeSelector}) => (
                    <button
                        type="button"
                        disabled={false}
                        onClick={showQuestionTypeSelector}
                        className="
                            mt-2 truncate rounded-sm bg-white px-4 py-1 text-sm
                            font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300
                            ring-inset
                            hover:bg-gray-50
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                        "
                    >
                        Add question
                    </button>
                )}
            </QuestionTypeSelector>
        </>
    );

    return (
        <ManagerTabs
            // title={`${workoutForm.values.name ?? ""} | ${props.group.values.name ?? ""}`}
            disableHistory
            extra={save}
            tabs={[
                {
                    name: "workout",
                    title: "Workout",
                    content: (
                        <Workout
                            workout_uuid={workout.uuid}
                            handlers={handlers}
                            isTemplate
                        />
                    ),
                    icon: "custom-dumbbell",
                },
                {
                    name: "details",
                    title: "Details",
                    hasErrors: workoutForm.errors != null,
                    content: (
                        <div className="flex flex-col gap-4">
                            <forms.PField field={workoutForm.fields.name} />
                            <forms.PField field={workoutForm.fields.instructions} />
                            {[].length > 1 && percentageExercises.length > 0 && (
                                <forms.PField field={workoutForm.fields.maxes} />
                            )}
                        </div>
                    ),
                    icon: "pencil-square-o",
                },
                {
                    name: "survey",
                    title: "Survey",
                    content: survey,
                    icon: "0814-list2",
                },
            ]}
        />
    );
}

type Route =
    | {
          route: "program";
      }
    | {
          route: "create_workout";
      }
    | {
          route: "update_workout";
          workout: server.journal.workouts.Workout.output;
          group: server.journal.workouts.WorkoutGroup.output;
      }
    | {
          route: "create_group";
      }
    | {
          route: "update_group";
          group: server.journal.workouts.WorkoutGroup.output;
      };

export const useProgram = (
    props: Pick<server.manager.templates.UpdateProgram, "clients"> & {
        program: server.journal.workouts.Program.output;
        setProgram: React.Dispatch<
            React.SetStateAction<server.journal.workouts.Program.output>
        >;
        embed?: {url: string; readOnly?: boolean; user_id: number};
    },
): {breadcrumbs: Breadcrumbs; content: React.ReactElement} => {
    const skipFirstSave = React.useRef(true);
    const {program, setProgram} = props;
    const {profile, request} = useSiteContext();

    const url = new URL(
        reverse("program_redirect", {product_code: program.product_code}),
        request.url,
    );
    const templateDialog =
        dialogs.useDialog<server.journal.workouts.WorkoutTemplate.output>({
            modal: dialogs.HModal,
            Content: TemplateDialog,
        });

    const urlForm = forms.usePform(
        {
            url: {type: "text"},
        },
        {url: url.href},
        () => {},
    );

    const programForm = forms.usePform(
        {
            name: {type: "text"},
            description: {type: "textarea"},
            visibility: {
                label: "Who can use this program?",
                type: "select",
                required: true,
                options: [
                    ["PRIVATE", "Private"],
                    ["PUBLIC", "Public"],
                    ["CLIENTS", "Share with my clients"],
                ],
            },
        },
        {...program, name: program.name ?? ""},
        (values) => {
            if (values.name == "") {
                skipFirstSave.current = true;
                programForm.setErrors({name: ["Name is required"]});
            } else {
                programForm.setErrors({name: undefined});
            }

            setProgram({...program, ...values});
        },
    );

    const clients = props.clients;
    const programUsers = program.instances
        .filter((u) => u.is_active == true)
        .map((pu) => {
            return {
                ...pu,
                client_manager: clients.find(
                    (client) => client.client.id == pu.user_id,
                ),
            };
        });

    const usersNotInProgram = clients.filter(
        (client) =>
            programUsers.find(
                (programUser) =>
                    programUser.client_manager?.client.id == client.client.id,
            ) == null,
    );

    const programUser =
        props.embed?.user_id != null
            ? {user_id: props.embed.user_id}
            : programUsers.length == 1
              ? {user_id: programUsers[0].user_id}
              : null;

    const [route, setRoute] = React.useState<Route>({route: "program"});

    useDebounce(
        () => {
            if (props.embed?.readOnly == true) {
                return;
            }

            if (skipFirstSave.current == true) {
                skipFirstSave.current = false;
                return;
            }

            void server.journal.workouts.update_program({
                uuid: program.uuid,
                creation: null,
                workout_groups: program.workout_groups,
                program_users: program.program_users,
                template: programForm.values,
                exercise_maxes: [],
            });
            void analytics.track("UPDATED_PROGRAM");
        },
        500,
        [program],
    );

    const previewDialog = dialogs.useDialog<
        unknown,
        {template: server.journal.workouts.Workout.output}
    >({
        Content: ({template, close}) => (
            <dialogs.HModal.Content
                title={<h2 className="text-lg font-semibold">{template.name}</h2>}
                actions={
                    <div className="flex gap-4">
                        <button
                            onClick={close}
                            type="button"
                            className="
                                order-2 rounded-md bg-white px-2 py-1.5 text-xs
                                font-semibold text-gray-900 shadow-xs ring-1
                                ring-gray-300 ring-inset
                                hover:bg-gray-50
                            "
                        >
                            Close
                        </button>
                    </div>
                }
                content={
                    <div className="space-y-4">
                        {annotateWithExercises(template).exercises.map((exercise) => (
                            <div key={exercise.uuid} className="space-y-2">
                                <h2>{exercise.type.name}</h2>
                                <ExerciseSetsPreview exercise={exercise} />
                            </div>
                        ))}
                    </div>
                }
            />
        ),

        modal: dialogs.HModal,
    });

    const copyWorkout = (
        groupIndex: number,
        group: server.journal.workouts.WorkoutGroup.output,
        workout: server.journal.workouts.WorkoutGroupWorkout.output,
    ) => {
        void analytics.track("COPIED_PROGRAM_WORKOUT");
        const workoutUUID = crypto.randomUUID();

        setProgram(
            produce(program, (draft) => {
                draft.workout_groups[groupIndex].workouts.push({
                    ...workout,
                    used_on: null,
                    sections: workout.sections.map((section) => ({
                        ...section,
                        uuid: crypto.randomUUID(),
                        groups: section.groups.map((group) => ({
                            ...group,
                            uuid: crypto.randomUUID(),
                            exercises: group.exercises.map((exercise) => ({
                                ...exercise,
                                uuid: crypto.randomUUID(),
                                sets: exercise.sets.map((set) => ({
                                    ...set,
                                    uuid: crypto.randomUUID(),
                                })),
                            })),
                        })),
                    })),
                    questions: workout.questions.map((question) => ({
                        ...question,
                        uuid: crypto.randomUUID(),
                    })),
                    uuid: workoutUUID,
                    template_instances: [],
                });
            }),
        );
    };

    const [scrollTo, setScrollTo] = React.useState<UUID | null>(null);

    React.useEffect(() => {
        if (scrollTo != null) {
            document.getElementById(scrollTo)!.scrollIntoView();

            setScrollTo(null);
        }
    }, [scrollTo]);

    const copyGroup = (group: server.journal.workouts.WorkoutGroup.output) => {
        void analytics.track("COPIED_PROGRAM_GROUP");

        const groupUUID = crypto.randomUUID();
        const workouts = group.workouts.map((workout) => ({
            ...workout,
            uuid: crypto.randomUUID(),
            used_on: null,
            sections: workout.sections.map((section) => ({
                ...section,
                groups: section.groups.map((group) => ({
                    ...group,
                    exercises: group.exercises.map((exercise) => ({
                        ...exercise,
                        sets: exercise.sets.map((set) => ({
                            ...set,
                            uuid: crypto.randomUUID(),
                        })),
                        uuid: crypto.randomUUID(),
                        rest_duration: null,
                    })),
                    uuid: crypto.randomUUID(),
                })),
                uuid: crypto.randomUUID(),
            })),
            questions: workout.questions.map((question) => ({
                ...question,
                uuid: crypto.randomUUID(),
            })),

            template_instances: [],
        }));

        const existingLabels = program.workout_groups.map((g) => g.name);
        setScrollTo(groupUUID);
        setProgram({
            ...program,
            workout_groups: [
                ...program.workout_groups,
                {
                    workouts,
                    name: utils.generateCopyLabel(group.name, existingLabels),
                    order: program.workout_groups.length,
                    uuid: groupUUID,
                    description: group.description,

                    id: 0,
                    user_id: 0,
                },
            ],
        });
    };

    const deleteGroup = (group: server.journal.workouts.WorkoutGroup.output) => {
        skipFirstSave.current = true;

        setProgram((program) => ({
            ...program,
            workout_groups: program.workout_groups.filter((g) => g.uuid != group.uuid),
        }));
        void processResponse(
            server.journal.workouts.delete_group({source_group: group.uuid}),
            {
                quiet: true,
            },
        );

        void analytics.track("DELETED_PROGRAM_GROUP");
    };

    const deleteWorkout = (workout: server.journal.workouts.Workout.output) => {
        skipFirstSave.current = true;
        setProgram((program) => ({
            ...program,
            workout_groups: program.workout_groups.map((group) => ({
                ...group,
                workouts: group.workouts.filter((w) => w.uuid != workout.uuid),
            })),
        }));

        void processResponse(
            server.journal.workouts.delete_workout({
                source_workout: workout.uuid,
            }),
            {
                quiet: true,
            },
        );

        void analytics.track("DELETED_PROGRAM_WORKOUT");
    };

    const createWorkout = (
        groupIndex: number,
        group: server.journal.workouts.WorkoutGroup.output,
    ) => {
        setProgram(
            produce(program, (draft) => {
                draft.workout_groups[groupIndex].workouts.push({
                    uuid: crypto.randomUUID(),
                    used_on: null,
                    instructions: "",
                    name: `Workout ${group.workouts.length + 1}`,
                    questions: [],
                    status: "INCOMPLETE",
                    maxes: "EXISTING_MAXES",
                    date: "",
                    mode: "NORMAL",
                    user_id: 0,
                    id: 0,
                    sections: [],
                    template_instances: [],
                    exercises_count: 0,
                });
            }),
        );
        void analytics.track("CREATED_PROGRAM_WORKOUT");
    };

    if (route.route == "create_group") {
        const back = () => setRoute({route: "program"});

        return {
            breadcrumbs: [
                {title: props.program.name ?? "", onClick: back},
                {label: "Create group"},
            ],
            content: (
                <GroupRoute
                    back={() => setRoute({route: "program"})}
                    group={null}
                    onSubmit={(values) => {
                        setRoute({route: "program"});
                        setProgram({
                            ...program,
                            workout_groups: [
                                ...program.workout_groups,
                                {
                                    name: values.name,
                                    description: values.description,
                                    workouts: [],
                                    order: program.workout_groups.length,
                                    uuid: crypto.randomUUID(),

                                    user_id: 0,
                                    id: 0,
                                },
                            ],
                        });
                    }}
                />
            ),
        };
    } else if (route.route == "update_group") {
        const back = () => setRoute({route: "program"});

        return {
            breadcrumbs: [
                {title: props.program.name ?? "", onClick: back},
                {label: route.group.name},
            ],
            content: (
                <GroupRoute
                    back={back}
                    group={route.group}
                    onSubmit={(values) => {
                        setRoute({route: "program"});
                        setProgram(
                            produce(program, (draftState) => {
                                const group = draftState.workout_groups.find(
                                    (g) => g.uuid == route.group.uuid,
                                )!;
                                group.name = values.name;
                                group.description = values.description;
                            }),
                        );
                    }}
                />
            ),
        };
    } else if (route.route == "update_workout") {
        const back = () => setRoute({route: "program"});

        return {
            breadcrumbs: [
                {title: props.program.name ?? "", onClick: back},
                {
                    label: route.group.name,
                },
                {label: route.workout.name ?? ""},
            ],
            content: (
                <ProgramUser.Provider value={programUser}>
                    <UpdateWorkout
                        embed={props.embed}
                        program_id={props.program.id}
                        group={route.group}
                        workout={route.workout}
                        back={back}
                    />
                </ProgramUser.Provider>
            ),
        };
    }

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const {source, destination} = result;

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        if (result.type === "WORKOUT") {
            void analytics.track("SORTED_PROGRAM_WORKOUTS");

            const reordered = produce(program, (draftState) => {
                const sourceGroup = draftState.workout_groups.find(
                    (group) => group.uuid == source.droppableId,
                )!;
                const destinationGroup = draftState.workout_groups.find(
                    (group) => group.uuid == destination.droppableId,
                )!;
                const sourceWorkout = sourceGroup.workouts.splice(source.index, 1)[0];
                destinationGroup.workouts.splice(destination.index, 0, sourceWorkout);
            });

            setProgram(reordered);
        } else if (result.type == "GROUP") {
            void analytics.track("SORTED_PROGRAM_GROUPS");

            const reordered = produce(program, (draftState) => {
                const sourceGroup = draftState.workout_groups.splice(
                    source.index,
                    1,
                )[0];
                draftState.workout_groups.splice(destination.index, 0, sourceGroup);
            });
            setProgram(reordered);
        }
    };

    const renderedWorkouts =
        (
            workoutGroupIndex: number,
            workoutGroup: server.journal.workouts.Program.output["workout_groups"][number],
        ) =>
        (provided: DroppableProvided) => (
            <ul
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col"
            >
                {workoutGroup.workouts
                    .map(annotateWithExercises)
                    .map((template, templateIndex) => (
                        <Draggable
                            key={template.uuid}
                            draggableId={template.uuid}
                            index={templateIndex}
                            isDragDisabled={program.program_users?.should_sync == true}
                        >
                            {(provided) => (
                                <li
                                    {...provided.draggableProps}
                                    ref={provided.innerRef}
                                    className={classNames(
                                        "col-span-1 rounded-lg bg-white border border-gray-200 shadow-xs relative my-2",
                                        Card.isComplete,
                                    )}
                                >
                                    <div
                                        className="
                                            flex w-full items-center justify-between
                                            space-x-4 p-4
                                        "
                                    >
                                        {program.program_users?.should_sync != true && (
                                            <DragHandler
                                                handle={provided.dragHandleProps}
                                            />
                                        )}
                                        <div className="flex-1 truncate">
                                            <div className="flex items-center space-x-3">
                                                <h3
                                                    className="
                                                        truncate text-sm font-medium
                                                        text-gray-900
                                                    "
                                                >
                                                    {template.name}
                                                </h3>
                                            </div>
                                            <p
                                                className="
                                                    mt-1 truncate text-sm text-gray-500
                                                "
                                            >
                                                <span>
                                                    {template.sections.length == 1
                                                        ? "1 section"
                                                        : `${template.sections.length} sections`}
                                                </span>{" "}
                                                and{" "}
                                                <span>
                                                    {template.exercises.length == 1
                                                        ? "1 exercise"
                                                        : `${template.exercises.length} exercises`}
                                                </span>
                                                <span>
                                                    {" "}
                                                    and{" "}
                                                    {template.questions.length == 1
                                                        ? "1 survey question"
                                                        : `${template.questions.length} survey questions`}
                                                </span>
                                            </p>
                                        </div>
                                        {props.embed != null &&
                                            template.template_instances.length > 0 && (
                                                <a
                                                    className="
                                                        rounded-sm bg-white px-2 py-1
                                                        text-xs font-semibold
                                                        text-gray-900 shadow-xs ring-1
                                                        ring-gray-300 ring-inset
                                                        hover:bg-gray-50
                                                    "
                                                    href={
                                                        reverse("client_days", {
                                                            client_manager_id:
                                                                template
                                                                    .template_instances[0]
                                                                    ?.user
                                                                    ?.client_manager
                                                                    ?.pk ?? 0,
                                                            date:
                                                                template
                                                                    .template_instances[0]
                                                                    ?.date ?? "",
                                                        }) + "?tab=workout"
                                                    }
                                                >
                                                    View
                                                </a>
                                            )}
                                        {(props.embed?.readOnly == true ||
                                            (props.embed != null &&
                                                template.template_instances.length ==
                                                    0 &&
                                                program.program_users?.should_sync ==
                                                    true)) && (
                                            <button
                                                onClick={() => {
                                                    void previewDialog({template});
                                                }}
                                                type="button"
                                                className="
                                                    rounded-sm bg-white px-2 py-1
                                                    text-xs font-semibold text-gray-900
                                                    shadow-xs ring-1 ring-gray-300
                                                    ring-inset
                                                    hover:bg-gray-50
                                                "
                                            >
                                                Preview
                                            </button>
                                        )}

                                        {props.embed?.readOnly != true &&
                                            program.program_users?.should_sync !=
                                                true && (
                                                <>
                                                    <button
                                                        onClick={() => {
                                                            /*
                                                    history.pushState(
                                                        {},
                                                        "",
                                                        `${window.location.pathname}?workout=${template.uuid}`,
                                                    );
                                                    */

                                                            setRoute({
                                                                route: "update_workout",
                                                                workout: template,
                                                                group: workoutGroup,
                                                            });
                                                        }}
                                                        type="button"
                                                        className="
                                                            rounded-md bg-indigo-600
                                                            px-2 py-1 text-xs
                                                            font-semibold text-white
                                                            shadow-xs
                                                            hover:bg-indigo-500
                                                            focus-visible:outline-2
                                                            focus-visible:outline-offset-2
                                                            focus-visible:outline-indigo-600
                                                            focus-visible:outline-solid
                                                            disabled:border-slate-200
                                                            disabled:bg-slate-50
                                                            disabled:text-slate-500
                                                            disabled:shadow-none
                                                        "
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            copyWorkout(
                                                                workoutGroupIndex,
                                                                workoutGroup,
                                                                template,
                                                            );
                                                        }}
                                                        type="button"
                                                        className="
                                                            rounded-md bg-indigo-600
                                                            px-2 py-1 text-xs
                                                            font-semibold text-white
                                                            shadow-xs
                                                            hover:bg-indigo-500
                                                            focus-visible:outline-2
                                                            focus-visible:outline-offset-2
                                                            focus-visible:outline-indigo-600
                                                            focus-visible:outline-solid
                                                            disabled:border-slate-200
                                                            disabled:bg-slate-50
                                                            disabled:text-slate-500
                                                            disabled:shadow-none
                                                        "
                                                    >
                                                        Copy
                                                    </button>
                                                </>
                                            )}
                                        {props.embed?.readOnly != true && (
                                            <button
                                                onClick={() => {
                                                    deleteWorkout(template);
                                                }}
                                                type="button"
                                                className="
                                                    rounded-md bg-indigo-600 px-2 py-1
                                                    text-xs font-semibold text-white
                                                    shadow-xs
                                                    hover:bg-indigo-500
                                                    focus-visible:outline-2
                                                    focus-visible:outline-offset-2
                                                    focus-visible:outline-indigo-600
                                                    focus-visible:outline-solid
                                                    disabled:border-slate-200
                                                    disabled:bg-slate-50
                                                    disabled:text-slate-500
                                                    disabled:shadow-none
                                                "
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </li>
                            )}
                        </Draggable>
                    ))}
                {provided.placeholder}
            </ul>
        );

    const renderedGroups = (provided: DroppableProvided) => (
        <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className="flex flex-col"
        >
            {program.workout_groups.map((workoutGroup, workoutGroupIndex) => (
                <Draggable
                    key={workoutGroup.uuid}
                    draggableId={workoutGroup.uuid}
                    index={workoutGroupIndex}
                    isDragDisabled={program.program_users?.should_sync == true}
                >
                    {(innerProvided) => (
                        <div
                            id={workoutGroup.uuid}
                            ref={innerProvided.innerRef}
                            {...innerProvided.draggableProps}
                        >
                            <div className="flex flex-wrap items-center gap-4 py-4">
                                {program.program_users?.should_sync != true && (
                                    <DragHandler
                                        handle={innerProvided.dragHandleProps}
                                    />
                                )}
                                <h2 className="flex-1 text-base font-semibold">
                                    {workoutGroup.name}
                                </h2>
                                {props.embed?.readOnly != true &&
                                    program.program_users?.should_sync != true && (
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setRoute({
                                                        route: "update_group",
                                                        group: workoutGroup,
                                                    })
                                                }
                                                className="
                                                    rounded-md bg-indigo-600 px-2 py-1
                                                    text-xs font-semibold text-white
                                                    shadow-xs
                                                    hover:bg-indigo-500
                                                    focus-visible:outline-2
                                                    focus-visible:outline-offset-2
                                                    focus-visible:outline-indigo-600
                                                    focus-visible:outline-solid
                                                    disabled:border-slate-200
                                                    disabled:bg-slate-50
                                                    disabled:text-slate-500
                                                    disabled:shadow-none
                                                "
                                            >
                                                Edit group
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => copyGroup(workoutGroup)}
                                                className="
                                                    rounded-md bg-indigo-600 px-2 py-1
                                                    text-xs font-semibold text-white
                                                    shadow-xs
                                                    hover:bg-indigo-500
                                                    focus-visible:outline-2
                                                    focus-visible:outline-offset-2
                                                    focus-visible:outline-indigo-600
                                                    focus-visible:outline-solid
                                                    disabled:border-slate-200
                                                    disabled:bg-slate-50
                                                    disabled:text-slate-500
                                                    disabled:shadow-none
                                                "
                                            >
                                                Copy group
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    deleteGroup(workoutGroup)
                                                }
                                                className="
                                                    rounded-md bg-indigo-600 px-2 py-1
                                                    text-xs font-semibold text-white
                                                    shadow-xs
                                                    hover:bg-indigo-500
                                                    focus-visible:outline-2
                                                    focus-visible:outline-offset-2
                                                    focus-visible:outline-indigo-600
                                                    focus-visible:outline-solid
                                                    disabled:border-slate-200
                                                    disabled:bg-slate-50
                                                    disabled:text-slate-500
                                                    disabled:shadow-none
                                                "
                                            >
                                                Delete group
                                            </button>
                                            <typography.DropdownButton
                                                onClick={() =>
                                                    createWorkout(
                                                        workoutGroupIndex,
                                                        workoutGroup,
                                                    )
                                                }
                                                items={[
                                                    {
                                                        icon: "custom-dumbbell",
                                                        title: "Use template",
                                                        onClick: async () => {
                                                            const result =
                                                                await templateDialog();

                                                            if (result != null) {
                                                                copyWorkout(
                                                                    workoutGroupIndex,
                                                                    workoutGroup,
                                                                    {
                                                                        ...result,
                                                                        template_instances:
                                                                            [],
                                                                        date: null,
                                                                        used_on: null,
                                                                        exercises_count: 0,
                                                                        maxes: "EXISTING_MAXES",
                                                                    },
                                                                );
                                                            }
                                                        },
                                                    },
                                                ]}
                                            >
                                                Add workout
                                            </typography.DropdownButton>
                                        </div>
                                    )}
                            </div>

                            <Droppable droppableId={workoutGroup.uuid} type="WORKOUT">
                                {renderedWorkouts(workoutGroupIndex, workoutGroup)}
                            </Droppable>
                        </div>
                    )}
                </Draggable>
            ))}
            {provided.placeholder}
        </div>
    );

    const programContent = (
        <>
            {previewDialog.dialog}
            {templateDialog.dialog}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" type="GROUP">
                    {renderedGroups}
                </Droppable>
            </DragDropContext>
            {props.embed?.readOnly != true &&
                program.program_users?.should_sync != true && (
                    <div className="mt-4 flex gap-4">
                        <button
                            type="button"
                            onClick={() =>
                                setRoute({
                                    route: "create_group",
                                })
                            }
                            className="
                                rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold
                                text-white shadow-xs
                                hover:bg-indigo-500
                                focus-visible:outline-2 focus-visible:outline-offset-2
                                focus-visible:outline-indigo-600
                                focus-visible:outline-solid
                                disabled:border-slate-200 disabled:bg-slate-50
                                disabled:text-slate-500 disabled:shadow-none
                            "
                        >
                            Add group
                        </button>
                    </div>
                )}
        </>
    );

    const detailsContent = (
        <div className="flex flex-col gap-2">
            <forms.PField field={programForm.fields.name} />
            <forms.PField field={programForm.fields.description} />
            <forms.PField
                field={{
                    ...programForm.fields.visibility,
                    disabledOptions: ["PUBLIC"],
                }}
            />

            {profile?.user.manager_slug != "" &&
                programForm.values.visibility == "CLIENTS" && (
                    <forms.PField
                        field={{
                            ...urlForm.fields.url,
                            readOnly: true,
                        }}
                    />
                )}
        </div>
    );

    const clientsContent = [].length > 1 && props.embed == null && (
        <div className="flex flex-col gap-2 py-2">
            <h2 className="text-xl font-semibold">Currently assigned</h2>
            <ul className="mt-2 space-y-4">
                {programUsers.map((client) => (
                    <li
                        key={client.uuid}
                        className={classNames(
                            "col-span-1 rounded-lg bg-white border border-gray-200 shadow-xs relative",
                        )}
                    >
                        <div
                            className="
                                flex w-full items-center justify-between space-x-4 p-4
                            "
                        >
                            <div className="flex-1 truncate">
                                <div className="flex items-center space-x-3">
                                    <h3
                                        className="
                                            truncate text-sm font-medium text-gray-900
                                        "
                                    >
                                        {client.client_manager?.name}
                                    </h3>
                                </div>

                                <p className="mt-1 truncate text-sm text-gray-500">
                                    {client.client_manager?.client.email}
                                </p>
                            </div>

                            <>
                                <button
                                    type="button"
                                    className="
                                        rounded-md bg-white px-2 py-1.5 text-xs
                                        font-semibold text-gray-900 shadow-xs ring-1
                                        ring-gray-300 ring-inset
                                        hover:bg-gray-50
                                    "
                                    onClick={() => {
                                        alert("Not implemented");
                                    }}
                                >
                                    Remove
                                </button>
                            </>
                        </div>
                    </li>
                ))}
            </ul>
            <h2 className="text-xl font-semibold">Add a client</h2>
            <ul className="mt-2 space-y-4">
                {usersNotInProgram.map((client) => (
                    <li
                        key={client.id}
                        className={classNames(
                            "col-span-1 rounded-lg bg-white border border-gray-200 shadow-xs relative",
                        )}
                    >
                        <div
                            className="
                                flex w-full items-center justify-between space-x-4 p-4
                            "
                        >
                            <div className="flex-1 truncate">
                                <div className="flex items-center space-x-3">
                                    <h3
                                        className="
                                            truncate text-sm font-medium text-gray-900
                                        "
                                    >
                                        {client.name}
                                    </h3>
                                </div>

                                <p className="mt-1 truncate text-sm text-gray-500">
                                    {client.client.email}
                                </p>
                            </div>

                            {client.client.active_program != null &&
                            client.client.active_program.program.id !=
                                props.program.id ? (
                                <a
                                    className="
                                        rounded-md bg-white px-2 py-1.5 text-xs
                                        font-semibold text-gray-900 shadow-xs ring-1
                                        ring-gray-300 ring-inset
                                        hover:bg-gray-50
                                    "
                                    href={reverse("program_detail", {
                                        program_id:
                                            client.client.active_program.program.id,
                                    })}
                                >
                                    In Other Program
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className="
                                        rounded-md bg-white px-2 py-1.5 text-xs
                                        font-semibold text-gray-900 shadow-xs ring-1
                                        ring-gray-300 ring-inset
                                        hover:bg-gray-50
                                    "
                                    onClick={() => {}}
                                >
                                    Add
                                </button>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );

    return {
        breadcrumbs: [{title: props.program.name ?? "", link: ""}],
        content:
            props.embed != null ? (
                programContent
            ) : (
                <ManagerTabs
                    title={programForm.values.name}
                    disableHistory
                    tabs={[
                        {
                            name: "workouts",
                            icon: "0814-list2",
                            title: "Workouts",
                            content: programContent,
                        },
                        {
                            name: "details",
                            title: "Details",
                            content: detailsContent,
                            icon: "pencil-square-o",
                        },
                        {
                            name: "clients",
                            title: "Clients",
                            content: clientsContent,
                            icon: "0291-users",
                        },
                    ]}
                />
            ),
    };
};

export const Template = (props: server.manager.templates.UpdateProgram) => {
    const [program, setProgram] = React.useState(props.program);
    const {breadcrumbs, content} = useProgram({
        program,
        setProgram,
        clients: props.clients,
    });

    return (
        <ManagerLayout
            title="Update program"
            narrow
            currentClientManagerId={null}
            breadcrumbs={[
                {title: "Programs", link: reverse("programs_list")},
                ...breadcrumbs,
            ]}
        >
            {content}
        </ManagerLayout>
    );
};
